/**
 * SubjectBinding summaries for the template picker (`SC-F513`, `SC-S513`).
 *
 * The template value builder is a picker rather than a text box because **the
 * paths that resolve depend on the binding mode**, and Sluice's own documentation
 * states the asymmetry backwards. `concept.md` §5.3 settles it, verified against
 * the pinned tree:
 *
 *   - The claim vocabulary is **closed at five keys** — `subjectId`, `email`,
 *     `groups`, `tenantId`, `allowedRegions`. An inline extras map is declared
 *     upstream and tagged `json:"-"` with no custom unmarshaler, and decoding runs
 *     YAML → JSON → `encoding/json`, so it is never populated in either mode
 *     (`SLUICE-U23`). There is no custom claim to author in this build.
 *   - On an **API-key** binding the declared values are literals: `tenantId` and
 *     `allowedRegions` are stamped onto the claim map under those exact names, so
 *     `{{ subject.tenantId }}` resolves to the literal.
 *   - On a **JWT** binding the mapping is consumed only for `subjectId`, `email`
 *     and `groups`; the claim map is a clone of the raw token claims. The
 *     consequence is *not* that `{{ subject.tenantId }}` fails to render —
 *     `renderSubject` falls through to a raw claim lookup, so it resolves whenever
 *     the token carries a claim literally named `tenantId`. The consequence is that
 *     the mapping **cannot rename a claim for template purposes**: a binding
 *     declaring `claims.tenantId: $.tenant` changes nothing, and the working
 *     template is `{{ subject.claims.tenant }}`.
 *
 * A template that fails to render at query time **denies the request** rather than
 * degrading to unfiltered rows. That is the correct security posture and the reason
 * the picker's job is to emit only paths that exist for the selected mode.
 */

import { getPolicy, listPolicies, type PolicyObject } from "../../api/policies.js";
import { asRecord } from "./model.js";

/** The five keys `spec.claims` may declare. The list is closed upstream, so it is
 * a constant here rather than a schema read — but the *help* for each still comes
 * from the schema's `claimPaths` group. */
export const CLAIM_KEYS = ["subjectId", "email", "groups", "tenantId", "allowedRegions"] as const;

/** ClaimKey is one of the five. */
export type ClaimKey = (typeof CLAIM_KEYS)[number];

/**
 * BindingSummary is everything the template picker needs about one
 * `SubjectBinding`, and nothing else.
 *
 * `acceptsJwt` mirrors `policymodel.SubjectBindingSpec.AcceptsJWT`: a binding
 * authenticates JWTs when it names a key source. A binding may do both, in which
 * case the picker must offer only what resolves in **both** modes — otherwise the
 * same policy denies every query from whichever half of the traffic the template
 * does not fit.
 */
export interface BindingSummary {
  name: string;
  acceptsJwt: boolean;
  acceptsApiKeys: boolean;
  /** The declared claim mappings, by key. Values are the JSONPath-lite paths the
   * binding wrote — which is a different grammar from a claim check's dot path. */
  claims: Partial<Record<ClaimKey, string>>;
  /** The declared API key ids, for the `subjects.apiKeys` completions. */
  apiKeyIds: string[];
  /** True when this build could not decode the stored spec, so nothing about the
   * binding may be asserted. */
  unreadable: boolean;
}

/** mode names which of the two credential paths a binding serves, for the copy
 * that has to say it. */
export function bindingMode(b: BindingSummary): "jwt" | "api-key" | "both" | "none" {
  if (b.acceptsJwt && b.acceptsApiKeys) return "both";
  if (b.acceptsJwt) return "jwt";
  if (b.acceptsApiKeys) return "api-key";
  return "none";
}

/** modeLabel is the picker's one-word description of a binding. */
export function modeLabel(b: BindingSummary): string {
  switch (bindingMode(b)) {
    case "jwt":
      return "JWT binding";
    case "api-key":
      return "API-key binding";
    case "both":
      return "JWT and API-key binding";
    default:
      return "no credential source";
  }
}

/** summarize projects one policy record onto a [BindingSummary]. */
export function summarize(obj: PolicyObject): BindingSummary {
  const spec = asRecord(obj.spec);
  const claimsRaw = asRecord(spec["claims"]);
  const claims: Partial<Record<ClaimKey, string>> = {};
  for (const key of CLAIM_KEYS) {
    const value = claimsRaw[key];
    if (typeof value === "string" && value !== "") claims[key] = value;
  }
  const keys = Array.isArray(spec["apiKeys"]) ? (spec["apiKeys"] as unknown[]) : [];
  return {
    name: obj.metadata.name,
    acceptsJwt:
      typeof spec["jwksUrl"] === "string" && spec["jwksUrl"] !== ""
        ? true
        : typeof spec["hmacSecretRef"] === "string" && spec["hmacSecretRef"] !== "",
    acceptsApiKeys: keys.length > 0,
    claims,
    apiKeyIds: keys
      .map((k) => asRecord(k)["id"])
      .filter((id): id is string => typeof id === "string" && id !== ""),
    unreadable: !!obj.unreadable,
  };
}

/**
 * loadBindings reads every `SubjectBinding` in the instance's draft and summarizes
 * it.
 *
 * The list route answers rows without specs — a list renders six columns and
 * shipping every spec would make it the largest response in the product — so the
 * bindings are read individually. That is one request per binding, which is
 * acceptable because a binding set is a handful of objects: an instance has one per
 * issuer plus one per agent fleet, not hundreds.
 *
 * A binding that cannot be read is dropped rather than guessed at, and the caller
 * learns how many by comparing against `total`. Guessing a mode would be the one
 * failure mode this whole feature exists to prevent.
 */
export async function loadBindings(
  instanceId: string,
): Promise<{ bindings: BindingSummary[]; total: number }> {
  const list = await listPolicies(instanceId, { kind: "SubjectBinding", limit: 200 });
  const names = list.items.filter((r) => r.enabled).map((r) => r.name);
  const loaded = await Promise.all(
    names.map((name) =>
      getPolicy(instanceId, "SubjectBinding", name)
        .then(summarize)
        .catch(() => null),
    ),
  );
  return {
    bindings: loaded.filter((b): b is BindingSummary => b !== null && !b.unreadable),
    total: names.length,
  };
}

/** TemplatePath is one option the picker may emit. */
export interface TemplatePath {
  /** The path inside the braces, e.g. `subject.tenantId`. */
  path: string;
  label: string;
  /** What it resolves to at query time, in the author's terms. */
  help: string;
  /** True for a path whose value is a list, which only makes sense with `In`. */
  list?: boolean;
}

/**
 * pathsFor returns the template paths that will actually resolve for one binding.
 *
 * The construction is subtractive rather than additive, and that is the point:
 * every path that is *not* offered is one an author cannot accidentally ship. Two
 * exclusions are load-bearing.
 *
 * `subject.tenantId` and `subject.allowedRegions` appear only when the binding
 * stamps them as literals — the API-key path. On a JWT binding the mapping is inert
 * for those two keys, so the honest offer is the underlying claim
 * (`subject.claims.<the path the issuer emits>`) and not the mapped name.
 *
 * `subject.issuer` appears only for a JWT binding. On the API-key path there is no
 * issuer, so the path renders the empty string — and an empty string is worse than
 * an unresolvable variable here: it does not deny, it silently filters every row
 * against `''`.
 */
export function pathsFor(binding: BindingSummary | undefined): TemplatePath[] {
  const out: TemplatePath[] = [
    {
      path: "subject.sub",
      label: "subject.sub",
      help: "The subject identifier: claims.subjectId when the binding sets it, and the API key id otherwise.",
    },
    {
      path: "subject.email",
      label: "subject.email",
      help: "The subject's email, from the binding's email mapping or the raw email claim.",
    },
    {
      path: "subject.groups",
      label: "subject.groups",
      help: "The subject's groups. This is a list, so it only makes sense with In or Not in.",
      list: true,
    },
    {
      path: "subject.auth_method",
      label: "subject.auth_method",
      help: "How the subject authenticated. Constant per credential class rather than per subject.",
    },
    {
      path: "subject.request_id",
      label: "subject.request_id",
      help: "The gateway's own request id. Unique per query, so a filter on it matches no stored row — useful only against a column that records it.",
    },
  ];
  if (!binding) return out;

  const mode = bindingMode(binding);
  if (mode === "jwt" || mode === "both") {
    out.push({
      path: "subject.issuer",
      label: "subject.issuer",
      help:
        mode === "both"
          ? "The token issuer — and empty for a request this binding authenticates with an API key, which would filter every row against an empty string rather than denying."
          : "The token issuer.",
    });
    // On the JWT path the mapping cannot rename a claim for template purposes, so
    // the offer is the claim as the issuer emits it — the mapping's own value with
    // its JSONPath-lite prefix stripped.
    for (const key of CLAIM_KEYS) {
      const declared = binding.claims[key];
      if (!declared) continue;
      const path = `subject.claims.${stripJsonPath(declared)}`;
      if (out.some((p) => p.path === path)) continue;
      out.push({
        path,
        label: path,
        help: `The claim this binding maps to ${key}. On the JWT path the mapping does not rename the claim for template purposes, so the template must name it as the issuer emits it.`,
      });
    }
  }
  if (mode === "api-key" || mode === "both") {
    for (const key of ["tenantId", "allowedRegions"] as const) {
      const declared = binding.claims[key];
      if (!declared) continue;
      out.push({
        path: `subject.${key}`,
        label: `subject.${key}`,
        help:
          mode === "both"
            ? `Stamped as the literal ${JSON.stringify(declared)} on the API-key path. On this binding's JWT path nothing reads the mapping, so it resolves only if the token carries a claim literally named ${key}.`
            : `Stamped onto the subject as the literal ${JSON.stringify(declared)}.`,
        list: key === "allowedRegions",
      });
    }
  }
  return out;
}

/** stripJsonPath removes the optional JSONPath-lite prefix a claim mapping may
 * carry, so `$.realm_access.roles` becomes `realm_access.roles` — the form a
 * template path takes. */
export function stripJsonPath(path: string): string {
  return path.replace(/^\$\.?/, "");
}

/** wrap turns a path into the whole-string template Sluice compiles. The spacing
 * is fixed, because the template is compared byte for byte in a digest and a
 * cosmetic difference would mint a new generation. */
export function wrap(path: string): string {
  return `{{ ${path} }}`;
}

/** unwrap reads the path back out of a template string, or returns the empty
 * string when it is not a whole-string reference. */
export function unwrap(template: string): string {
  const t = template.trim();
  if (!t.startsWith("{{") || !t.endsWith("}}")) return "";
  return t.slice(2, -2).trim();
}
